// pages/events.js — Events log page.
//
// Owns the `data-page="events"` section: every event, a page at a time,
// fetched from the server with the page's own search, category chips and
// type dropdown, so all events are reachable and the count is exact. Team
// changes (member joins, invites) come from the team's admin log and are
// placed among the events by time. When a number on another page opened
// this page (state.eventQuery, event-links.js), it shows exactly that
// number's events instead.
import { state } from "../state.js";
import { isTeamUnreachable, teamUnavailableHTML } from "../team-status.js";
import { $, patchHTML, loadingHTML } from "../dom.js";
import { eventRowHTML, eventCategory, friendlyEvent, eventGroup, HIDDEN_EVENT_TYPES, numericEventId } from "../format.js";
import { escapeHTML, parseUTC } from "../util.js";
import { EVENTS_PAGE_SIZE } from "../constants.js";
import { api } from "../api.js";
import { withBuFilter } from "../bu-switcher.js";
import { decryptEventRow } from "../decrypt-helpers.js";

// ---- the type dropdown ----

// Types seen in the last 30 days, from the server's per-type counts, so the
// dropdown offers types older than the current page too.
const typeCounts = { at: 0, types: [] };

async function loadTypes() {
  if (typeCounts.loading || Date.now() - typeCounts.at < 5 * 60 * 1000) return;
  typeCounts.loading = true;
  try {
    const d = await api(withBuFilter("/v1/teams/events/decision-counts?since=30d"));
    typeCounts.types = Object.keys((d && d.by_event_type) || {});
    typeCounts.at = Date.now();
    if (state.activePage === "events") rebuildSubFilter();
  } catch {
    // The dropdown keeps the types it had; the list itself is unaffected.
  } finally {
    typeCounts.loading = false;
  }
}

// rebuildSubFilter repopulates the second-level type dropdown, narrowed to
// the active category, grouped and icon-prefixed like the rows.
function rebuildSubFilter() {
  const sel = $("event-subfilter");
  if (!sel) return;
  const category = state.eventTypeFilter || "all";
  const seen = new Set();
  for (const et of typeCounts.types) {
    if (!et || HIDDEN_EVENT_TYPES.has(et)) continue;
    if (category !== "all" && eventCategory(et) !== category) continue;
    seen.add(et);
  }
  if (state.eventSubTypeFilter !== "all") seen.add(state.eventSubTypeFilter);
  const types = Array.from(seen).sort();
  if (types.length <= 1 && state.eventSubTypeFilter === "all") {
    if (sel.style.display !== "none") sel.style.display = "none";
    if (sel.dataset.sig) {
      sel.innerHTML = "";
      delete sel.dataset.sig;
    }
    return;
  }
  // Only rewrite the options when they or the selection changed, so a poll
  // tick never collapses an open dropdown.
  const sig = state.eventSubTypeFilter + "|" + types.join(",");
  if (sel.dataset.sig === sig) {
    if (sel.style.display === "none") sel.style.display = "";
    return;
  }
  sel.style.display = "";
  const byGroup = new Map();
  for (const t of types) {
    const g = eventGroup(t);
    if (!byGroup.has(g.key)) byGroup.set(g.key, { label: g.label, icon: g.icon, types: [] });
    byGroup.get(g.key).types.push(t);
  }
  const ORDER = ["collision", "contention", "preemption", "stale", "negotiation", "lifecycle", "membership", "system"];
  const opts = [`<option value="all">All types</option>`];
  for (const key of ORDER) {
    const g = byGroup.get(key);
    if (!g) continue;
    opts.push(`<optgroup label="${escapeHTML(decodeEntity(g.icon) + "  " + g.label)}">`);
    for (const t of g.types.sort()) {
      const label = friendlyEvent(t).label || t;
      const selAttr = t === state.eventSubTypeFilter ? " selected" : "";
      opts.push(`<option value="${escapeHTML(t)}"${selAttr}>${escapeHTML(label)} (${escapeHTML(t)})</option>`);
    }
    opts.push(`</optgroup>`);
  }
  sel.innerHTML = opts.join("");
  sel.dataset.sig = sig;
}

// decodeEntity converts a numeric HTML entity ("&#8856;") to its glyph, since
// <option> labels render literal text.
function decodeEntity(ent) {
  const m = /^&#(\d+);$/.exec(ent || "");
  return m ? String.fromCodePoint(Number(m[1])) : "";
}

// ---- server requests ----

// browseSpec is the server filter for the page's own controls.
export function browseSpec() {
  const spec = { exclude: [...HIDDEN_EVENT_TYPES] };
  const cat = state.eventTypeFilter || "all";
  if (cat !== "all") spec.category = cat;
  if (state.eventSubTypeFilter && state.eventSubTypeFilter !== "all") spec.types = [state.eventSubTypeFilter];
  const q = (state.eventFilterText || "").trim();
  // "#<id>" matches one event; list rows carry ids like "c297701".
  if (q) spec.q = q.startsWith("#") ? "#" + numericEventId(q.slice(1)) : q;
  return spec;
}

// eventsURL is the request for `limit` events from `offset`.
export function eventsURL(q, offset, limit) {
  const p = new URLSearchParams();
  if (q.types && q.types.length) p.set("type", q.types.join(","));
  if (q.since) p.set("since", q.since);
  if (q.until) p.set("until", q.until);
  if (q.agent) p.set("agent", q.agent);
  if (q.resource) p.set("resource", q.resource);
  if (q.actor) p.set("actor_subject", q.actor);
  if (q.q) p.set("q", q.q);
  if (q.category) p.set("category", q.category);
  if (q.exclude && q.exclude.length) p.set("exclude", q.exclude.join(","));
  p.set("limit", String(limit));
  p.set("offset", String(offset));
  if (q.source === "local") return "/v1/events?" + p.toString();
  return withBuFilter("/v1/teams/events?" + p.toString());
}

// queryURL is one page of a number's events.
export function queryURL(q, page) {
  return eventsURL(q, page * EVENTS_PAGE_SIZE, EVENTS_PAGE_SIZE);
}

// Team changes (the admin log) matching the browse filter, every one of
// them: there are few, and they are placed among the events by time.
const audit = { key: "", rows: null, at: 0 };

async function loadAudit(spec) {
  const key = JSON.stringify([spec.q || "", spec.category || "", (spec.types || [])[0] || ""]);
  if (audit.key === key && audit.rows && Date.now() - audit.at < 60 * 1000) return audit.rows;
  const p = new URLSearchParams();
  if (spec.q && !spec.q.startsWith("#")) p.set("q", spec.q);
  if (spec.category) p.set("category", spec.category);
  const rows = [];
  // An exact event id never matches a team change.
  if (!(spec.q && spec.q.startsWith("#"))) {
    for (let offset = 0; ; offset += 200) {
      p.set("limit", "200");
      p.set("offset", String(offset));
      const d = await api("/v1/teams/audit?" + p.toString());
      const page = (d && d.entries) || [];
      rows.push(...page);
      if (page.length < 200 || typeof d.total !== "number" || rows.length >= d.total) break;
    }
  }
  const want = (spec.types || [])[0];
  audit.rows = rows.map(auditRow).filter((r) => !want || r.EventType === want);
  audit.key = key;
  audit.at = Date.now();
  return audit.rows;
}

function auditRow(e) {
  const payload = { actor_id: e.actor_id || "", target: e.target || "", detail: e.detail || "" };
  return {
    EventType: e.action || "audit.unknown",
    event_type: e.action || "audit.unknown",
    OccurredAt: e.created_at,
    occurred_at: e.created_at,
    Resource: e.detail || e.target || "",
    resource: e.detail || e.target || "",
    AgentID: "",
    agent_id: "",
    EventID: "a" + e.id,
    event_id: "a" + e.id,
    Payload: payload,
    payload,
  };
}

const at = (e) => parseUTC(e.occurred_at || e.OccurredAt);

// placeAudit picks the team changes that belong on one page of events:
// those between this page's oldest event and the previous page's oldest
// (newer ones go on page 1, older ones on the last page).
export function placeAudit(auditRows, pageRows, prevRow, isLast) {
  const upper = prevRow ? at(prevRow) : Infinity;
  const lower = isLast || !pageRows.length ? -Infinity : at(pageRows[pageRows.length - 1]);
  return auditRows.filter((a) => {
    const t = at(a);
    return t < upper && t >= lower;
  });
}

// ---- one list, either mode ----

const list = { key: "", rows: null, total: 0, error: "", at: 0, timer: 0, lastQ: "" };

async function loadList(key, number, spec, page) {
  try {
    let rows;
    let total;
    if (number) {
      const d = await api(queryURL(spec, page));
      if (typeof d.total !== "number") throw new Error("the server did not say how many events match; update AgentCollision");
      rows = await Promise.all((d.events || []).map(decryptEventRow));
      total = d.total;
    } else {
      // One extra event before the page marks where the previous page ended.
      const offset = Math.max(0, page * EVENTS_PAGE_SIZE - 1);
      const limit = EVENTS_PAGE_SIZE + (page > 0 ? 1 : 0);
      const [d, auditRows] = await Promise.all([api(eventsURL(spec, offset, limit)), loadAudit(spec)]);
      if (typeof d.total !== "number") throw new Error("the server did not say how many events match; update AgentCollision");
      let evs = await Promise.all((d.events || []).map(decryptEventRow));
      const prev = page > 0 ? evs.shift() : null;
      const isLast = (page + 1) * EVENTS_PAGE_SIZE >= d.total;
      rows = [...evs, ...placeAudit(auditRows, evs, prev, isLast)].sort((a, b) => at(b) - at(a));
      total = d.total + auditRows.length;
      list.eventsTotal = d.total;
      showSearchScope(d.search_covers);
    }
    if (list.key !== key) return;
    list.rows = rows;
    list.total = total;
    list.error = "";
    list.at = Date.now();
  } catch (err) {
    if (list.key !== key) return;
    list.rows = null;
    list.error = (err && err.message) || String(err);
  }
  if (state.activePage === "events") renderEvents();
}

// showSearchScope says what the search box can find: everything on this
// computer's own events, but only the type and chat on the team server,
// where file names and details are encrypted.
function showSearchScope(covers) {
  const input = $("event-filter");
  const help = $("events-help");
  const team = covers === "type_and_chat";
  if (input) input.placeholder = team ? "Search by event type or chat…" : "Search by event type, chat, file, or detail…";
  const text = help && help.querySelector("span");
  if (text)
    text.innerHTML = team
      ? "Searches every event by <strong>event type</strong> and <strong>chat</strong>, and team changes by member and detail. File names aren't searchable here: the team server only stores them encrypted. Chips narrow to a category."
      : "Searches every event by <strong>event type</strong>, <strong>chat</strong>, <strong>file</strong> and <strong>detail</strong>. Chips narrow to a category.";
}

function showNumberChrome(on) {
  const banner = $("events-query");
  if (banner) banner.hidden = !on;
  for (const id of ["events-filter-bar", "events-help"]) {
    const el = $(id);
    if (el) el.style.display = on ? "none" : "";
  }
}

export function queryBannerHTML(q, total, loading) {
  const count = loading ? "" : total.toLocaleString() + (total === 1 ? " event" : " events");
  return (
    '<div class="ev-query-text"><span class="ev-query-label">Showing</span> <strong>' +
    escapeHTML(q.label || "Filtered events") + "</strong>" +
    (count ? ' <span class="ev-query-count">' + count + "</span>" : "") + "</div>" +
    '<button type="button" class="btn btn-sm" data-ev-clear="1">Show all events</button>'
  );
}

function pageCount() {
  const n = state.eventQuery ? list.total : list.eventsTotal || 0;
  return Math.max(1, Math.ceil(n / EVENTS_PAGE_SIZE));
}

// renderEvents paints the current page.
export function renderEvents() {
  const wrap = $("events-list");
  const pager = $("events-pagination");
  if (!wrap) return;
  const number = !!state.eventQuery;
  const spec = number ? state.eventQuery : browseSpec();
  const page = number ? state.eventQueryPage : state.eventPage;
  const key = JSON.stringify([number, spec, page]);
  showNumberChrome(number);
  if (!number) {
    rebuildSubFilter();
    loadTypes();
  }
  // Page 1 of all events refreshes as new ones arrive.
  const stale = !number && page === 0 && list.rows && Date.now() - list.at > 15000;
  if (list.key !== key || stale) {
    const typing = !number && (spec.q || "") !== list.lastQ;
    if (list.key !== key) {
      list.rows = null;
      list.error = "";
    }
    list.key = key;
    list.lastQ = spec.q || "";
    clearTimeout(list.timer);
    // Wait for a pause in typing before asking the server.
    list.timer = setTimeout(() => loadList(key, number, spec, page), typing ? 250 : 0);
  }
  if (number) {
    const banner = $("events-query");
    if (banner) patchHTML(banner, queryBannerHTML(spec, list.total, list.rows === null && !list.error));
  }
  if (list.error) {
    list.shown = false;
    wrap.classList.remove("ev-busy");
    patchHTML(wrap, isTeamUnreachable() ? teamUnavailableHTML("Events") : `<div class="empty"><div class="empty-title">Could not load events</div><div class="empty-detail">${escapeHTML(list.error)}</div></div>`);
    if (pager) patchHTML(pager, "");
    return;
  }
  // While the next page loads, keep the current rows on screen, dimmed.
  wrap.classList.toggle("ev-busy", list.rows === null && !!list.shown);
  if (list.rows === null) {
    if (!list.shown) {
      patchHTML(wrap, loadingHTML("events"));
      if (pager) patchHTML(pager, "");
    }
    return;
  }
  list.shown = true;
  if (list.rows.length === 0) {
    list.shown = false;
    const filtered = number || spec.q || spec.category || spec.types;
    patchHTML(
      wrap,
      `<div class="empty"><div class="empty-title">No events</div><div class="empty-detail">${
        filtered ? "Nothing matches. Try a different filter." : "Events appear here as agents acquire and release leases."
      }</div></div>`,
    );
    if (pager) patchHTML(pager, "");
    return;
  }
  const start = page * EVENTS_PAGE_SIZE;
  patchHTML(wrap, list.rows.map((e, i) => eventRowHTML(e, false, start + i)).join(""));
  if (!pager) return;
  const totalPages = pageCount();
  const prev = page === 0 ? "disabled" : "";
  const next = page >= totalPages - 1 ? "disabled" : "";
  patchHTML(
    pager,
    `<div class="info">Page <strong style="color: var(--text);">${page + 1}</strong> of ${totalPages.toLocaleString()} ·
      <strong style="color: var(--text);">${list.total.toLocaleString()}</strong> ${!number && spec.q ? "matching " : ""}events</div>
    <div class="controls">
      <button data-eq-page="first" ${prev}>&laquo; First</button>
      <button data-eq-page="prev" ${prev}>&lsaquo; Prev</button>
      <span class="page-indicator">Page ${page + 1} / ${totalPages}</span>
      <button data-eq-page="next" ${next}>Next &rsaquo;</button>
      <button data-eq-page="last" ${next}>Last &raquo;</button>
    </div>`,
  );
}

function onQueryClick(e) {
  if (e.target.closest("[data-ev-clear]")) {
    state.eventQuery = null;
    state.eventQueryPage = 0;
    renderEvents();
    return;
  }
  const btn = e.target.closest("[data-eq-page]");
  if (!btn || btn.disabled) return;
  const number = !!state.eventQuery;
  const cur = number ? state.eventQueryPage : state.eventPage;
  const last = pageCount() - 1;
  const page = Math.min(last, Math.max(0, { first: 0, prev: cur - 1, next: cur + 1, last }[btn.dataset.eqPage]));
  if (number) state.eventQueryPage = page;
  else state.eventPage = page;
  renderEvents();
}

// ---- mount ----
//
// Two render triggers:
//   1. Section becomes `.active` (user navigates here) — MutationObserver.
//   2. Polling tick in dashboard.js dispatches `dashboard:tick`
//      on document — we re-render if our section is active.
//
// Legacy renderEvents in src/bootstrap.js still runs on each
// tick, so this render is currently redundant. patchHTML is
// idempotent so the double-render is wasted work but not visible.
// Will be the only render after bootstrap.js is fully
// deleted.

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  renderEvents();
  tickHandler = () => {
    if (state.activePage === "events") renderEvents();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="events"]');
  if (!section) return;
  section.addEventListener("click", onQueryClick);
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
